import React, { useCallback } from "react";
import { Streaming } from "../../../types";
import { Button } from "../../UI/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "../../common/Grid";
import { Input, Label, Radio } from "../../UI/Forms/FormElements";
import { FormControl } from "@mui/material";
import { observer } from "mobx-react";
import Feedback from "../../UI/Forms/FormElements/Feedback";
import { useDisclosure } from "react-use-disclosure";
import { FileUploadModal } from "../../common/FileUploadModal";

type Props = {
  streaming?: Streaming;
  isSubmitting: boolean;
  onSubmit: (values: Streaming) => void;
};

const StreamingForm: React.FC<Props> = observer(
  ({ streaming, isSubmitting, onSubmit }) => {
    const { t } = useTranslation();
    const {
      isOpen: isModalUploadOpen,
      open: openModalUpload,
      close: closeModalUpload,
    } = useDisclosure();

    const formik = useFormik({
      initialValues: {
        id: streaming?.id || undefined,
        active: streaming?.active || false,
        coverUrl: streaming?.coverUrl || "",
        url: streaming?.url || "",
      },
      enableReinitialize: true,
      onSubmit: onSubmit,
      validationSchema: Yup.object().shape({
        coverUrl: Yup.string().required(
          t("components.common.streamingForm.coverUrlRequired")
        ),
        url: Yup.string().required(
          t("components.common.streamingForm.urlRequired")
        ),
      }),
    });

    const handleUploadPoster = useCallback(
      async (imageUrls: string[]) => {
        formik.setFieldValue("coverUrl", imageUrls[0]);
        closeModalUpload();
      },
      [formik, closeModalUpload]
    );

    return (
      <Container paddingBottom={"10rem"}>
        <form onSubmit={formik.handleSubmit}>
          <Container pb={"20px"}>
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="coverUrl">
                    {t("components.common.streamingForm.coverUrl")}
                  </Label>
                  {formik.values.coverUrl.length > 0 ? (
                    <img
                      src={formik.values.coverUrl}
                      alt={formik.values.coverUrl}
                    />
                  ) : (
                    <p>
                      {t("components.common.streamingForm.coverUrlPlaceholder")}
                    </p>
                  )}
                  {formik.errors.coverUrl && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("coverUrl").error}
                    </Feedback>
                  )}
                  <Button color="light" onClick={openModalUpload}>
                    {t("common.upload")}
                  </Button>
                </FormControl>
              </Col>
            </Row>
            <Row mt="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="url">
                    {t("components.common.streamingForm.url")}
                  </Label>
                  <Input
                    type="text"
                    id="url"
                    name="url"
                    placeholder={t(
                      "components.common.streamingForm.urlPlaceholder"
                    )}
                    value={formik.values.url}
                    onChange={formik.handleChange}
                    state={formik.errors.url ? "error" : "success"}
                    showState={true}
                    feedbackText={formik.getFieldMeta("url").error}
                  />
                </FormControl>
              </Col>
            </Row>
            <Row paddingTop="10px">
              <Col noGutter>
                <FormControl fullWidth>
                  <Label htmlFor="active">
                    {t("components.common.streamingForm.state")}
                  </Label>
                  <Radio
                    id="active"
                    name="active"
                    value={"active"}
                    label={t("components.common.streamingForm.active")}
                    onChange={() => {
                      formik.setFieldValue("active", true);
                    }}
                    checked={formik.values.active}
                  />
                  <Radio
                    id="active2"
                    name="active2"
                    value={"active"}
                    label={t("components.common.streamingForm.notActive")}
                    onChange={() => {
                      formik.setFieldValue("active", false);
                    }}
                    checked={!formik.values.active}
                  />

                  {formik.errors.active && (
                    <Feedback
                      state="error"
                      showState={true}
                      showErrorOnly={true}
                    >
                      {formik.getFieldMeta("active").error}
                    </Feedback>
                  )}
                </FormControl>
              </Col>
            </Row>
          </Container>

          <Row>
            <Col textAlign="center">
              <Button type={"submit"} disabled={isSubmitting}>
                {t("common.save")}
              </Button>
            </Col>
          </Row>

          <FileUploadModal
            isOpen={isModalUploadOpen}
            onClose={closeModalUpload}
            onFileUploaded={handleUploadPoster}
            maxFiles={1}
            accept="image/jpeg, image/png"
          />
        </form>
      </Container>
    );
  }
);

export default StreamingForm;
